import { Checkbox, Input, Modal, Radio, Select, Typography } from 'antd'
import { apiGet, urls } from 'api'
import { IcSearch } from 'assets/icons'
import { SparepartListFooter, Table } from 'components'
import { SEARCH_DEBOUNCE_MS, SPAREPART_MODAL_WIDTH } from 'constant'
import useDebounce from 'hooks/useDebounce'
import useInfiniteScrollSentinel from 'hooks/useInfiniteScrollSentinel'
import useInfiniteSpareparts from 'hooks/useInfiniteSpareparts'
import { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'
import { formatCurrency } from 'utils'

const DEFAULT_MODE = 'default'

const SPAREPART_DETAIL_COLUMNS = [
  {
    name: 'Sparepart',
    selector: (row) => row?.name,
    cell: (row) => (
      <div className="flex flex-col space-y-1">
        <Typography.Text className="font-medium text-gray-500 text-[11px] py-1">
          {row?.sku}
        </Typography.Text>
        <Typography.Text className="font-medium">{row?.name}</Typography.Text>
      </div>
    )
  },
  {
    name: 'Harga Layanan',
    selector: (row) => (row?.price ? formatCurrency(row?.price ?? 0) : '-')
  }
]

const toIdList = (value) => (Array.isArray(value) ? value : [])

const mergeIds = (current, ids) => Array.from(new Set([...toIdList(current), ...ids]))

const removeIds = (current, ids) => {
  const removedIds = new Set(ids)
  return toIdList(current).filter((id) => !removedIds.has(id))
}

const toSearchText = (value) => String(value ?? '').toLowerCase()

// Local search for default mode — matches the keyword against name or SKU
const filterSparepartsBySearch = (spareparts, search) => {
  const keyword = toSearchText(search).trim()
  if (!keyword) return spareparts
  return spareparts.filter(
    (row) => toSearchText(row?.name).includes(keyword) || toSearchText(row?.sku).includes(keyword)
  )
}

const ModalSparepart = ({
  isOpen,
  onCancel,
  type,
  formik,
  selected,
  setSelected,
  listData,
  setListData,
  valueSparepartDefault,
  onPickSpareparts = () => {},
  defaultSparepartOptions
}) => {
  const isSparepartMode = type !== DEFAULT_MODE
  const token = useSelector((state) => state.auth.token)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [category, setCategory] = useState('')
  const {
    spareparts,
    isLoading,
    isError,
    isFetchingNextPage,
    canLoadMore,
    fetchNextPage,
    refetch
  } = useInfiniteSpareparts({
    keywords: debouncedSearch,
    categoryId: category || undefined,
    isEnabled: isOpen && isSparepartMode
  })
  const sentinelRef = useInfiniteScrollSentinel(canLoadMore, fetchNextPage)

  useDebounce(() => setDebouncedSearch(search), [search], SEARCH_DEBOUNCE_MS)

  // Get sparepart categories
  useQuery({
    queryKey: ['sparepart-categories-list'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await apiGet({
        token,
        url: `${urls?.service?.prefix}/${urls?.service?.serviceManagement?.sparepartCategories?.all}?keywords=${search}`
      })
      return res
    },
    onSuccess: (res) => {
      setListData((prev) => ({ ...prev, sparepartCategories: res?.resData?.data ?? [] }))
    }
  })

  const defaultRows = useMemo(
    () => filterSparepartsBySearch(defaultSparepartOptions ?? [], search),
    [defaultSparepartOptions, search]
  )
  const rows = isSparepartMode ? spareparts : defaultRows
  const selectedIdSet = useMemo(() => new Set(toIdList(selected)), [selected])
  const isAllLoadedSelected = rows.length > 0 && rows.every((row) => selectedIdSet.has(row?.id))

  // Select-all applies to the rows loaded so far (the list is paginated)
  const handleChangeCheckAll = (e) => {
    const loadedIds = rows.map((row) => row?.id)
    if (!e?.target?.checked) {
      setSelected((prev) => removeIds(prev, loadedIds))
      return
    }
    setSelected((prev) => mergeIds(prev, loadedIds))
    onPickSpareparts(rows)
  }

  const handleChangeCheck = (e, row) => {
    if (!e?.target?.checked) {
      setSelected((prev) => removeIds(prev, [row?.id]))
      return
    }
    setSelected((prev) => mergeIds(prev, [row?.id]))
    onPickSpareparts([row])
  }

  const handleChangeRadio = (value) => setSelected(value)

  const columnsSparepart = [
    {
      name: (
        <Checkbox
          id={`checkbox-all`}
          name="item-all"
          className="custom-checkbox"
          aria-label="Pilih semua sparepart yang tampil"
          onChange={handleChangeCheckAll}
          checked={isAllLoadedSelected}
        />
      ),
      width: '50px',
      cell: (row) => (
        <Checkbox
          id={`checkbox-${row?.id}`}
          name="item"
          className="custom-checkbox"
          value={row?.id}
          onChange={(e) => handleChangeCheck(e, row)}
          checked={selectedIdSet.has(row?.id)}
        />
      )
    },
    ...SPAREPART_DETAIL_COLUMNS
  ]

  const columnsSparepartDefault = [
    {
      name: '',
      width: '50px',
      cell: (row) => (
        <Radio
          id={`radio-${row?.id}`}
          name="item"
          className="custom-radio-btn"
          value={row?.id}
          onChange={(e) => handleChangeRadio(e?.target?.value)}
          checked={selected === row?.id ? true : false}
        />
      )
    },
    ...SPAREPART_DETAIL_COLUMNS
  ]

  const resetFilters = () => {
    setSearch('')
    setDebouncedSearch('')
    setCategory('')
    setSelected('')
  }

  const handleSubmit = () => {
    if (type === DEFAULT_MODE) {
      formik?.setFieldValue('default_sparepart_id', selected)
    } else {
      const selectedIds = toIdList(selected)
      formik?.setFieldValue('sparepart_ids', selectedIds)
      if (!selectedIds.includes(valueSparepartDefault)) {
        formik?.setFieldValue('default_sparepart_id', '')
      }
    }
    resetFilters()
  }

  const handleCancel = () => {
    onCancel()
    resetFilters()
  }

  const handleConfirm = () => {
    onCancel()
    handleSubmit()
  }

  return (
    <Modal
      width={SPAREPART_MODAL_WIDTH}
      centered
      title={isSparepartMode ? 'Attach Item/Sparepart' : 'Pilih Sparepart Default'}
      open={isOpen}
      onCancel={handleCancel}
      onOk={handleConfirm}
      okText={'Confirm'}
      okButtonProps={{ className: 'bg-primary-500 rounded-md', type: 'danger' }}
      cancelButtonProps={{
        className: 'hover:text-[#000] rounded-md hover:border-[#000]',
        type: 'default'
      }}>
      <div className="bg-gray-50 p-2 rounded-lg">
        <Input
          className="rounded-md py-1.5 mb-1"
          placeholder="Cari Layanan"
          aria-label="Cari Layanan"
          prefix={<IcSearch className="text-gray-400" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {isSparepartMode ? (
          <Select
            className="custom-table-limit"
            style={{ width: '100%' }}
            value={category}
            onChange={(e) => setCategory(e)}
            placeholder="Kategori Sparepart"
            aria-label="Kategori sparepart">
            <Select.Option value="">Semua Kategori Sparepart</Select.Option>
            {listData?.sparepartCategories?.map((category, idx) => (
              <Select.Option key={idx} value={category?.id}>
                {category?.name}
              </Select.Option>
            ))}
          </Select>
        ) : null}
        <div className="overflow-y-auto overscroll-y-none h-40 mt-2">
          <Table
            columns={isSparepartMode ? columnsSparepart : columnsSparepartDefault}
            data={rows}
            pending={isSparepartMode && isLoading}
            hidePagination={true}
          />
          {isSparepartMode ? (
            <SparepartListFooter
              sentinelRef={sentinelRef}
              canLoadMore={canLoadMore}
              isFetchingNextPage={isFetchingNextPage}
              isError={isError}
              onRetry={refetch}
            />
          ) : null}
        </div>
      </div>
    </Modal>
  )
}

export default ModalSparepart
